/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { FormContext } from './Form';
import useDebounce from '../utility/debounce';
import Heading from './Heading';
import Icon from './Icon';
import Button from './Button';
import Row from '../layout/Row';
import { shortHandDate } from '../utility/functions';
import ModalFactory from '../Modal';
import Pre from '../layout/Pre';

const Wrapper = styled.div`
    display: flex;
    flex-direction: ${({ direction }) => direction};
`;

const PreCSS = styled.textarea`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.2em;
    border: 1px solid ${({ value, theme }) => value ? theme.gray : `${theme.gray}40`};
    border-radius: 0.2rem;
    user-select: none;
    color: ${({ theme }) => theme.text};
    transition: background-color 0.3s;
    background-color: ${({ theme }) => theme.input.background};
    padding: 0.3em 0.5em;
    font-size: inherit;
    font-family: inherit;
    height: 7em;
    resize: none;

    &:disabled {
        color: ${({ theme }) => theme.input.disabledText};
        background-color: ${({ theme }) => theme.input.disabled};
        transform: unset;
        box-shadow: unset;
        cursor: default;
    }
    
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    &::placeholder {
        color: ${({ theme }) => theme.input.disabled}40;
    }
    &:focus {
        outline: none !important;
        border: 1px solid ${({ theme }) => theme.text};
        box-shadow: 0 0 3px ${({ theme }) => theme.highlight};
    }
`;

const Tag = styled.span`
    font-size: 0.6em;
`;

const Error = styled(Tag)`
    color: ${({ theme }) => theme.error};
`;

const OptionalFlag = styled.i`
    color: ${({ theme }) => theme.gray};
    font-weight: 500;
    font-size: 0.8em;
`;

const ShowPassword = styled(Icon)`
    position: relative;
`;

const Preview = styled(Pre)`
    // margin: 0;
    // font-family: inherit;
    // p {
    //     margin: 0;
    // }
`;

const previewText = (input) => {
    const dummyTags = [
        {
            tag: '{ Account.DisplayName }',
            dummy: 'Hamilton'
        },
        {
            tag: '{ CompanyName }',
            dummy: 'Hamilton'
        },
        {
            tag: '{ Recipient.FullName }',
            dummy: 'Jain Doe'
        },
        {
            tag: '{ Recipient.FirstName }',
            dummy: 'Jain'
        },
        {
            tag: '{ Recipient.LastName }',
            dummy: 'Doe'
        },
        {
            tag: '{ FirstName }',
            dummy: 'John'
        },
        {
            tag: '{ LastName }',
            dummy: 'Doe'
        },
        {
            tag: '{ Referrer.FirstName }',
            dummy: 'John'
        },
        {
            tag: '{ Referrer.LastName }',
            dummy: 'Doe'
        },
        {
            tag: '{ Referrer.FullName }',
            dummy: 'John Doe'
        },
        {
            tag: '{ FullName }',
            dummy: 'John Doe'
        },
        {
            tag: '{ PassProgram }',
            dummy: 'Basic Wash'
        },
        {
            tag: '{ Recipient.AwardName }',
            dummy: 'Basic Wash'
        },
        {
            tag: '{ PassProgram }',
            dummy: 'Basic Plan'
        },
        {
            tag: '{ AwardName }',
            dummy: 'Basic Wash'
        },
        {
            tag: '{ UsesRemaining }',
            dummy: '3'
        },
        {
            tag: '{ UseCount }',
            dummy: '5'
        },
        {
            tag: '{ MaxUses }',
            dummy: '7'
        },
        {
            tag: '{ KioskItemName }',
            dummy: 'A Wash'
        },
        {
            tag: '{ ExpirationDate }',
            dummy: shortHandDate(new Date().toDateString())
        },
    ];

    dummyTags.forEach(e => input = input.replaceAll(e.tag, e.dummy));
    ModalFactory.show({
        title: 'Preview',
        body: <Preview>{input}</Preview>
    })
}

/**
 * Form - A custom form component with built-in state management and validation.
 *
 * @component
 * @param {Object} props - The properties passed to the component.
 * @param {Object} props.inputs - Initial values for the form fields as a key-value pair object.
 * @param {Function} props.onSubmit - Callback function triggered on form submission.
 * @param {boolean} [props.displayform=true] - Determines if the form should be displayed.
 * @param {boolean} [props.row=false] - Sets the layout direction of the form; `true` for row, `false` for column.
 * @param {React.ReactNode|Function} [props.children] - Form children components or a function that receives form context.
 * @param {boolean} [props.editable=false] - Toggles between editable and non-editable states for the form.
 * @param {boolean} [props.clearInputs=false] - Clears input fields after successful form submission.
 * @param {boolean} [props.localForm=false] - If `true`, handles the form submission locally without asynchronous operations.
 * @param {boolean} [props.windowSubmission] - If provided, registers a global function for programmatic form submission.
 * @param {boolean} [props.hideEdit=false] - Hides the edit button when `true`.
 * @param {boolean} [props.hideError=false] - Hides the error display when `true`.
 * @param {string} [props.name] - Name of the form, used for help documentation and context.
 * @param {Object} [props.roles] - Roles to check for user authorization to access/edit the form.
 * @param {React.ReactNode} [props.formControls] - Additional form controls displayed alongside the submit button.
 * @param {Object} [props.buttonstyle] - Inline styles for the form buttons.
 * @param {string} [props.submitting='Submitting'] - Text displayed on the submit button during submission.
 * @param {string} [props.text='Submit'] - Text displayed on the submit button in its default state.
 *
 * @returns {React.ReactElement} The rendered form component.
 */
const Preput = ({ context, ...props }) => {
    const [invalid, setInvalid] = useState(false);
    const [invalidMessage, setInvalidMessage] = useState('');
    const debouncedValue = useDebounce(context?.values[props.name], props.debounce);
    const [isFirstRender, setIsFirstRender] = useState(true);
    const [caret, setCaret] = useState(-1);

    useEffect(() => {
        if (!context) return;
        const { values } = context;
        if (props.confirmation) {
            context.setErrors(v => {
                return {
                    ...v,
                    [props.name]: values[props.confirmation] !== values[props.name]
                }
            });
        }
    }, [context?.values])

    useEffect(() => {
        if (!context) return;
        validate(debouncedValue);
    }, [debouncedValue])

    const handleInput = (e) => {
        e = e.target.value;

        if (props.clamp) {
            e = e < props.min ? props.min : e;
            e = e > props.max ? props.max : e;
        }

        const newValue = props.sanitize
            ? typeof props.sanitize === 'function'
                ? props.sanitize(e)
                : e.replace(props.sanitize, '')
            : e;

        context.setValues(v => {
            return {
                ...v,
                [props.name]: newValue
            }
        })
    }

    const validate = async (val) => {
        let invalid = false;
        if (val === '' && props.required) {
            isFirstRender ? setIsFirstRender(false) : setInvalidMessage('Please complete this fields.')
            invalid = true;
        }

        if (props.validate && val) {
            if (Array.isArray(props.validate)) {
                props.validate.forEach(e => {
                    if (!invalid) {
                        invalid = !(val.defaultMatch(e.regex) !== '');
                        if (invalid) setInvalidMessage(e.message);
                    }
                })
            } else if (typeof props.validate === 'function') {
                invalid = await new Promise((resolve) => props.validate(val, setInvalidMessage, resolve))
            } else {
                invalid = !(val.defaultMatch(props.validate) === val);
            }
        };

        if (props.disableError) {
            invalid = false;
        }
        if (props.disableErrorMessage) {
            setInvalidMessage('')
        }

        setInvalidation(invalid)
    }

    const setInvalidation = (invalid) => {
        setInvalid(invalid);
        context.setErrors(v => {
            return {
                ...v,
                [props.name]: invalid
            }
        });
    }

    const handleCaret = (event) => {
        const textarea = event.target;
        const position = textarea.selectionStart;
        setCaret(position);
    };

    const addTag = (tag) => {
        let currentValue = context ? context.values[props.name] : props.value;
        const modifiedString = currentValue.substring(0, caret) + `{ ${tag} }` + currentValue.substring(caret);
        setCaret(caret + `{ ${tag} }`.length);
        if (context) {
            context.setValue(props.name, modifiedString);
        } else {
            props.value = modifiedString
        }
    }

    return props.standalone ? (
        <Wrapper direction={props.row === true ? 'row' : 'column'}>
            <Heading fontSize={0.8}>
                {props.label}
                {context && !props.required && <OptionalFlag>- Optional</OptionalFlag>}
                {invalid && <Error>{invalidMessage || props.error}</Error>}
            </Heading>
            <PreCSS
                onKeyUp={handleCaret}
                value={(context ? context.values[props.name] : props.value) || ''}
                onInput={context && handleInput}
                disabled={context ? (context.submitting || context.editing || props.disable) : props.disable}
                onClick={handleCaret}

                {...props}
            />
            <Row style={{ paddingTop: '0.5em' }} wrap>
                {props.tags && (
                    <Button
                        icon='magnifyingGlass'
                        onClick={() => previewText((context ? context.values[props.name] : props.value) || '')}
                        label='Preview'
                    />
                )}

                {!context?.editing
                    ? props.tags?.map((e, i) => (
                        <Button
                            key={`Tag-${i}`}
                            onClick={() => addTag(e)}
                            label={e}
                        />
                    ))
                    : null
                }
            </Row>
        </Wrapper>
    ) : (
        <PreContext
            standalone={1}
            {...props}
        />
    );
}

export default Preput;

const PreContext = ({ ...props }) => {
    const context = useContext(FormContext);
    return (
        <Preput
            context={{ ...context }}
            {...props}
        />
    )
}
